import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.mobile-menu')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const isActive = (path) => location.pathname === path;
  
  // Navigation items - exactly like original SPI structure
  const navItems = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/advisory-board", label: "Advisory Board" },
    { to: "/research-2026", label: "Research & Development 2026" },
    { to: "/nil-u", label: "NIL U" }
  ];

  const moreItems = [
    { to: "/outputs-toolkit", label: "Outputs and Toolkit" },
    { to: "/support-spi", label: "Support SPI (inactive)", inactive: true }
  ];
  
  // Determine if we're on the home page to apply transparency effect
  const isHomePage = location.pathname === '/';
  
  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      isHomePage && !isScrolled 
        ? 'bg-transparent' 
        : 'bg-white backdrop-blur-sm bg-white/95'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Left side: Hamburger Menu + Logo */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Hamburger Menu Button - Mobile Optimized */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-1.5 sm:p-2 rounded-md transition-all duration-200 ${
                isMobileMenuOpen ? 'text-black hover:bg-gray-100' :
                isHomePage && !isScrolled 
                  ? 'text-white hover:bg-white hover:bg-opacity-10' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Logo - Mobile Optimized */}
            <div className="flex-shrink-0 flex items-center">
              <img 
                src="/assets/Logo.Sonom.Performance.Institute.png"
                alt="Sonom Performance Institute"
                className="h-8 w-8 sm:h-10 sm:w-10 mr-2 sm:mr-3"
              />
              <Link to="/" className={`text-lg sm:text-xl lg:text-2xl font-light hover:opacity-80 transition-all duration-300 tracking-tight ${
                isMobileMenuOpen ? 'text-black' : 
                isHomePage && !isScrolled ? 'text-white drop-shadow-lg' : 'text-black'
              }`}>
                Sonom Performance Institute
              </Link>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
            {navItems.slice(1).map((item) => ( // Skip "Home" in desktop nav
              <Link 
                key={item.to}
                to={item.to} 
                className={`text-sm lg:text-base font-medium transition-all duration-300 py-2 relative ${
                  isActive(item.to) 
                    ? (isHomePage && !isScrolled ? 'text-white drop-shadow-lg' : 'text-black')
                    : (isHomePage && !isScrolled ? 'text-gray-200 hover:text-white drop-shadow-lg' : 'text-gray-600 hover:text-black')
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* More Menu - Desktop */}
            <div className="relative group">
              <button className={`text-sm lg:text-base font-medium transition-all duration-300 py-2 ${
                isHomePage && !isScrolled ? 'text-gray-200 hover:text-white drop-shadow-lg' : 'text-gray-600 hover:text-black'
              }`}>
                More
                <svg className="w-4 h-4 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute right-0 mt-2 w-48 lg:w-56 bg-white rounded-xl shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                {moreItems.map((item) => (
                  <Link 
                    key={item.to}
                    to={item.to} 
                    className={`block px-3 lg:px-4 py-2 text-xs lg:text-sm hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                      item.inactive ? 'text-gray-400' : 'text-gray-700'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile Menu Dropdown - Optimized */}
      {isMobileMenuOpen && (
        <div className="md:hidden mobile-menu">
          <div className="px-4 sm:px-6 pt-2 pb-3 space-y-1 bg-white">
            {[...navItems, ...moreItems].map((item) => (
              <Link 
                key={item.to}
                to={item.to}
                className={`block px-3 py-2.5 rounded-md text-sm sm:text-base font-medium transition-colors ${
                  isActive(item.to)
                    ? 'bg-gray-100 text-black' 
                    : item.inactive 
                      ? 'text-gray-400' 
                      : 'text-gray-700 hover:bg-gray-50 hover:text-black'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;